import { useState, useEffect, lazy, Suspense } from "react";

const CommandPalette = lazy(() =>
  import("./CommandPalette").then((m) => ({ default: m.CommandPalette }))
);

/**
 * Deliberately tiny and always mounted eagerly in Layout -- just a
 * keydown listener and a boolean. The actual command palette UI (which
 * pulls in the real cmdk library, not a trivial dependency) is
 * lazy-loaded and never requested until someone actually presses Cmd+K
 * or clicks the header's shortcut hint for the first time.
 *
 * This split is the direct fix for a real, measured regression: the
 * full palette used to be mounted eagerly here, and a live PageSpeed
 * run showed Total Blocking Time at 2500ms on mobile vs 30ms on desktop
 * for the exact same page -- a signature of extra JavaScript execution
 * cost, not a network or asset problem. Every page was paying that cost
 * on load whether or not anyone ever opened the palette at all.
 */
export function CommandPaletteTrigger() {
  const [open, setOpen] = useState(false);
  const [hasEverOpened, setHasEverOpened] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        setHasEverOpened(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Not just gating the initial render -- this listener alone is what
  // the header's "⌘K" hint button relies on too (it dispatches a
  // synthetic keydown event rather than needing its own separate wiring
  // into the real component), so that button keeps working correctly
  // even before the lazy chunk has ever been requested.
  if (!hasEverOpened) return null;

  return (
    <Suspense fallback={null}>
      <CommandPalette open={open} onOpenChange={setOpen} />
    </Suspense>
  );
}
